import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdminHttpClient } from '../http.js';
import type { AdminAuditEvent, AdminOverview } from '../types.js';
import { toCredits } from '../utils/format.js';

export function OverviewScreen({ http }: { http: AdminHttpClient }) {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<AdminOverview>();
  const [audit, setAudit] = useState<AdminAuditEvent[]>([]);

  useEffect(() => {
    void Promise.all([http.overview(), http.listAudit()]).then(([nextOverview, nextAudit]) => {
      setOverview(nextOverview);
      setAudit(nextAudit.slice(0, 4));
    });
  }, [http]);

  return (
    <section className="content-stack">
      <p className="screen-help">{t('screenHelp.overview')}</p>
      <div className="metric-grid">
        <Metric label={t('overview.activeUsers')} value={overview?.users ?? 0} />
        <Metric label={t('overview.groups')} value={overview?.groups ?? 0} />
        <Metric label={t('overview.managedModels')} value={overview?.catalogModels ?? 0} />
        <Metric label={t('overview.gatewayBackends')} value={overview?.gatewayBackends ?? 0} />
        {overview?.credit && (
          <>
            <Metric
              label={overview.partnerName ? t('overview.creditsFromPartner', { name: overview.partnerName }) : t('overview.creditsFromPlatform')}
              value={overview.credit.unlimited ? t('allocationPanel.unlimited') : toCredits(overview.credit.granted ?? 0)}
            />
            <Metric
              label={t('overview.creditsRemaining')}
              value={overview.credit.unlimited ? t('allocationPanel.unlimited') : toCredits(overview.credit.remaining ?? 0)}
            />
          </>
        )}
      </div>
      <section className="panel">
        <h2>{t('overview.recentChanges')}</h2>
        <div className="event-list">
          {audit.length ? (
            audit.map((event) => (
              <div key={event.id} className="event-row">
                <span>{event.eventType}</span>
                <code>{event.id}</code>
              </div>
            ))
          ) : (
            <div className="muted">{t('overview.noAuditEvents')}</div>
          )}
        </div>
      </section>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}
